import appsignal from '../application/appsignal'

/**
 * Error Handler Mixin
 *
 * Provides consistent error reporting to AppSignal for Stimulus controllers.
 * Can be used with ApplicationController or as a mixin for third-party controllers.
 *
 * Usage with ApplicationController:
 * ```javascript
 * import ApplicationController from './application_controller'
 *
 * export default class extends ApplicationController {
 *   connect() {
 *     try {
 *       this.doSomething()
 *     } catch (error) {
 *       this.reportError(error, { action: 'connect' })
 *     }
 *   }
 * }
 * ```
 *
 * Usage with third-party controllers:
 * ```javascript
 * import Dropdown from '@stimulus-components/dropdown'
 * import { withErrorHandler } from '../mixins/error_handler'
 *
 * export default class extends withErrorHandler(Dropdown) {
 *   connect() {
 *     try {
 *       super.connect()
 *     } catch (error) {
 *       this.reportError(error)
 *     }
 *   }
 * }
 * ```
 *
 * @param {class} BaseClass - The base class to extend (usually Controller)
 * @returns {class} Extended class with reportError method
 */
export const withErrorHandler = (BaseClass) =>
  class extends BaseClass {
    /**
     * Reports an error to AppSignal with controller context and logs to console
     *
     * @param {Error} error - The error object to report
     * @param {string|Object} messageOrContext - Optional user-friendly message or context object
     * @param {Object} context - Additional context (if second param is a message)
     *
     * Usage:
     * - reportError(error) - Basic error reporting
     * - reportError(error, { key: 'value' }) - With context tags
     * - reportError(error, 'User-friendly message', { key: 'value' }) - With message and context
     */
    reportError(error, messageOrContext = {}, context = {}) {
      // Handle both signatures: (error, context) and (error, message, context)
      let message = ''
      let tags = {}

      if (typeof messageOrContext === 'string') {
        message = messageOrContext
        tags = context
      } else {
        tags = messageOrContext
      }

      // Always log to console for debugging
      if (message) {
        console.error(`${this.identifier}: ${message}`, error, tags)
      } else {
        console.error(`${this.identifier}:`, error, tags)
      }

      // Report to AppSignal with controller identifier and custom tags
      appsignal.sendError(error, (span) => {
        // Action drives AppSignal incident grouping; without it every controller collapses into one incident.
        span.setAction(this.identifier)
        span.setTags({
          controller: this.identifier,
          ...tags,
        })
      })
    }
  }
